import { isMarketingContactsSku, isMarketingContactsSkuV2 } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { RECURRING } from '../constants/ChargeTypes';
import { FLAT, TIERED } from '../constants/PricingTypes';
import { QUANTITY_PACK } from '../constants/SkuTypes';
import { isOnboardingFoundationSku } from '../core/utilities/isOnboardingFoundationSku';
import { roundAmount } from '../core/utilities/roundingUtils';
import { validatePurchaseConfig, validatePurchaseConfigV2 } from '../core/validators';
import { ONBOARDING_FOUNDATIONS } from '../constants/ApiNames';
import { isQuantityPackDetails } from '../core/TypeGuards';

/**
 * @deprecated productId be deprecated in favor of productApiName
 * use _isRecurringProductV2 instead.
 */
const _isRecurringProduct = ({
  productMap,
  skuMap,
  skuId
}) => productMap[skuMap[skuId].productId].chargeType === RECURRING;
const _isRecurringProductV2 = ({
  productApiNameMap,
  skuMap,
  skuId
}) => {
  return productApiNameMap[skuMap[skuId].productApiName].chargeType === RECURRING;
};

/**
 * @deprecated productMap uses productId which will be deprecated in favor of productApiName
 * Use calculatedPriceAdapterV4 instead.
 */
export const calculatedPriceAdapterV3 = ({
  purchaseConfig,
  productMap,
  skuMap
}) => {
  validatePurchaseConfig(purchaseConfig, productMap, skuMap);
  const {
    currencyCode,
    discountPercentage,
    productConfigurations = []
  } = purchaseConfig;
  const _getFlatPrice = ({
    pricingWrap
  }, quantity) => {
    const priceInfo = pricingWrap.prices.filter(price => {
      return price.currencyCode === currencyCode;
    })[0];
    if (!priceInfo) {
      return 0;
    }
    return priceInfo.value * quantity;
  };
  const _getTieredPrice = (sku, quantity) => {
    const {
      pricingWrap,
      skuId
    } = sku;
    const priceInfo = pricingWrap.tieredPrices.filter(price => {
      return price.currencyCode === currencyCode;
    })[0];
    if (!priceInfo) {
      return 0;
    }
    let quantityMultiplier = 1;
    if (sku.skuType === QUANTITY_PACK) {
      quantityMultiplier = sku.includedWithQuantityPack;
    }
    return priceInfo.priceTiers.reduce((totalPrice, priceTier) => {
      const units = Math.ceil(quantity / quantityMultiplier);
      const {
        upperBound,
        lowerBound
      } = priceTier;
      if (upperBound !== null && units > upperBound) {
        const isLowerBoundExclusive = (isMarketingContactsSku({
          skuMap,
          skuId
        }) || isOnboardingFoundationSku({
          skuMap,
          skuId
        })) && lowerBound === 0;
        return totalPrice + (upperBound - lowerBound + (isLowerBoundExclusive ? 0 : 1)) * priceTier.value;
      }
      if (units >= lowerBound) {
        const inclusiveLowerBound = lowerBound === 0 ? lowerBound : lowerBound - 1;
        return totalPrice + (units - inclusiveLowerBound) * priceTier.value;
      }
      return totalPrice;
    }, 0);
  };
  const _getPrice = ({
    quantity,
    skuId
  }) => {
    const sku = skuMap[skuId];
    if (!sku) {
      return 0;
    }
    if (sku.pricingWrap.pricingType === FLAT) {
      return _getFlatPrice(sku, quantity);
    }
    if (sku.pricingWrap.pricingType === TIERED) {
      return _getTieredPrice(sku, quantity);
    }
    return 0;
  };
  const _applyDiscount = fullPrice => {
    const discount = discountPercentage * 0.01;
    const exactPrice = fullPrice - fullPrice * discount;
    const roundedPrice = roundAmount(exactPrice, currencyCode);
    return roundedPrice;
  };
  const _formatByTermLength = (aggregate, itemPrice, index) => {
    const {
      skuId,
      quantity
    } = productConfigurations[index];
    const isRecurringProduct = _isRecurringProduct({
      productMap,
      skuMap,
      skuId
    });
    if (isRecurringProduct) {
      aggregate.monthly = {
        fullPrice: aggregate.monthly.fullPrice + itemPrice,
        calculatedPrice: aggregate.monthly.calculatedPrice + _applyDiscount(itemPrice),
        priceBreakdown: [...aggregate.monthly.priceBreakdown, {
          skuId,
          quantity,
          fullPrice: itemPrice,
          calculatedPrice: _applyDiscount(itemPrice)
        }]
      };
      aggregate.annually = {
        fullPrice: aggregate.annually.fullPrice + itemPrice * 12,
        calculatedPrice: aggregate.annually.calculatedPrice + _applyDiscount(itemPrice * 12),
        priceBreakdown: [...aggregate.annually.priceBreakdown, {
          skuId,
          quantity,
          fullPrice: itemPrice * 12,
          calculatedPrice: _applyDiscount(itemPrice * 12)
        }]
      };
      return aggregate;
    }
    const nonRecurringTotalPrice = aggregate.oneTimeFee.fullPrice + itemPrice;
    const nonRecurringCalculatedPrice = aggregate.oneTimeFee.fullPrice + _applyDiscount(itemPrice);
    aggregate.oneTimeFee = {
      fullPrice: nonRecurringTotalPrice,
      calculatedPrice: nonRecurringCalculatedPrice,
      priceBreakdown: [...aggregate.oneTimeFee.priceBreakdown, {
        skuId,
        quantity,
        fullPrice: itemPrice,
        calculatedPrice: _applyDiscount(itemPrice)
      }]
    };
    return aggregate;
  };
  return productConfigurations.map(_getPrice).reduce(_formatByTermLength, {
    monthly: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    },
    annually: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    },
    oneTimeFee: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    }
  });
};
export const calculatedPriceAdapterV4 = ({
  purchaseConfig,
  productApiNameMap,
  skuMap
}) => {
  validatePurchaseConfigV2(purchaseConfig, productApiNameMap, skuMap);
  const {
    currencyCode,
    discountPercentage,
    productConfigurations = []
  } = purchaseConfig;
  const _getFlatPrice = (sku, quantity) => {
    const priceInfo = sku.pricingWrap.prices.filter(price => {
      return price.currencyCode === currencyCode;
    })[0];
    if (!priceInfo) {
      return 0;
    }
    return priceInfo.value * quantity;
  };
  const _getTieredPrice = (sku, quantity) => {
    const {
      pricingWrap,
      skuId
    } = sku;
    const priceInfo = pricingWrap.tieredPrices.filter(price => {
      return price.currencyCode === currencyCode;
    })[0];
    if (!priceInfo) {
      return 0;
    }
    let quantityMultiplier = 1;
    if (isQuantityPackDetails(sku) && sku.includedWithQuantityPack) {
      quantityMultiplier = sku.includedWithQuantityPack;
    }
    return priceInfo.priceTiers.reduce((totalPrice, priceTier) => {
      const units = Math.ceil(quantity / quantityMultiplier);
      const {
        upperBound,
        lowerBound
      } = priceTier;
      if (upperBound !== null && upperBound !== undefined && units > upperBound) {
        // The pricing structure changed with marketing contacts SKUs, context here:
        // https://hubspot.slack.com/archives/CV5RT7CN8/p1588275759046600
        const isLowerBoundExclusive = (isMarketingContactsSkuV2({
          skuMap,
          skuId
        }) || sku.productApiName === ONBOARDING_FOUNDATIONS) && lowerBound === 0;
        return totalPrice + (upperBound - lowerBound + (isLowerBoundExclusive ? 0 : 1)) * priceTier.value;
      }
      if (units >= lowerBound) {
        const inclusiveLowerBound = lowerBound === 0 ? lowerBound : lowerBound - 1;
        return totalPrice + (units - inclusiveLowerBound) * priceTier.value;
      }
      return totalPrice;
    }, 0);
  };
  const _getPrice = ({
    quantity,
    skuId
  }) => {
    const sku = skuMap[skuId];
    if (!sku) {
      return 0;
    }
    if (sku.pricingWrap.pricingType === FLAT) {
      return _getFlatPrice(sku, quantity);
    }
    if (sku.pricingWrap.pricingType === TIERED) {
      return _getTieredPrice(sku, quantity);
    }
    return 0;
  };
  const _applyDiscount = fullPrice => {
    const discount = discountPercentage * 0.01;
    const exactPrice = fullPrice - fullPrice * discount;
    const roundedPrice = roundAmount(exactPrice, currencyCode);
    return roundedPrice;
  };
  const _formatByTermLength = (aggregate, itemPrice, index) => {
    const {
      skuId,
      quantity
    } = productConfigurations[index];
    const isRecurringProduct = _isRecurringProductV2({
      productApiNameMap,
      skuMap,
      skuId
    });
    if (isRecurringProduct) {
      aggregate.monthly = {
        fullPrice: aggregate.monthly.fullPrice + itemPrice,
        calculatedPrice: aggregate.monthly.calculatedPrice + _applyDiscount(itemPrice),
        priceBreakdown: [...aggregate.monthly.priceBreakdown, {
          skuId,
          quantity,
          fullPrice: itemPrice,
          calculatedPrice: _applyDiscount(itemPrice)
        }]
      };
      aggregate.annually = {
        fullPrice: aggregate.annually.fullPrice + itemPrice * 12,
        calculatedPrice: aggregate.annually.calculatedPrice + _applyDiscount(itemPrice * 12),
        priceBreakdown: [...aggregate.annually.priceBreakdown, {
          skuId,
          quantity,
          fullPrice: itemPrice * 12,
          calculatedPrice: _applyDiscount(itemPrice * 12)
        }]
      };
      return aggregate;
    }
    const nonRecurringTotalPrice = aggregate.oneTimeFee.fullPrice + itemPrice;
    const nonRecurringCalculatedPrice = aggregate.oneTimeFee.fullPrice + _applyDiscount(itemPrice);
    aggregate.oneTimeFee = {
      fullPrice: nonRecurringTotalPrice,
      calculatedPrice: nonRecurringCalculatedPrice,
      priceBreakdown: [...aggregate.oneTimeFee.priceBreakdown, {
        skuId,
        quantity,
        fullPrice: itemPrice,
        calculatedPrice: _applyDiscount(itemPrice)
      }]
    };
    return aggregate;
  };
  return productConfigurations.map(_getPrice).reduce(_formatByTermLength, {
    monthly: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    },
    annually: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    },
    oneTimeFee: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    }
  });
};